import type { PreviewBackground, RgbColor } from '../types.js';

export interface ImagePoint {
  x: number;
  y: number;
}

export type PointerToolHandler = (point: ImagePoint, event: PointerEvent, phase: 'down' | 'move' | 'up') => void;

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.25;

/**
 * Renders an RGBA image inside a fixed display box: the box itself never moves or resizes with
 * the image. A freshly loaded image is always fit-to-box and centered. Inside the box the user can
 *   - zoom: the +/− buttons, the mouse scroll wheel, or a two-finger pinch (zooming about the
 *     cursor / the pinch midpoint, so the detail under the fingers stays put);
 *   - pan a zoomed-in image: a sideways swipe, a two-finger touch drag,
 *     a middle-button or space+drag, or a one-finger drag when the pan ("Move") tool is active or
 *     no tool is wired up at all (the DPI preview).
 * The image can never be pushed out of the box: an axis that fits is kept centered, an axis that
 * overflows is clamped so its edges never leave the box. Tool clicks map through the current
 * scale + pan, so there is no separate state to drift out of sync.
 */
export class CanvasView {
  readonly container: HTMLElement;
  private readonly stage: HTMLDivElement;
  private readonly bgCanvas: HTMLCanvasElement;
  private readonly imgCanvas: HTMLCanvasElement;
  private width = 0;
  private height = 0;
  private baseScale = 1;
  private zoomFactor = 1;
  private scale = 1;
  private offsetX = 0;
  private offsetY = 0;
  /** Pan in container pixels relative to the centered position; clamped in layout(). */
  private panX = 0;
  private panY = 0;
  private background: PreviewBackground = 'checkerboard';
  private customColor: RgbColor = { r: 255, g: 255, b: 255 };
  private toolHandler: PointerToolHandler | null = null;
  private panTool = false;
  /** The active tool is a single click (wand, eyedropper): a left-drag pans instead, and the click fires on release if the mouse didn't move. */
  private clickTool = false;
  private pendingClick: { pointerId: number; x: number; y: number } | null = null;
  private spaceHeld = false;
  private isPointerDown = false;
  private panPointerId: number | null = null;
  private panLast: { x: number; y: number } | null = null;
  private resizeDebounce: ReturnType<typeof setTimeout> | undefined;
  private onZoomChange: ((percent: number) => void) | null = null;
  private readonly activePointers = new Map<number, { x: number; y: number }>();
  private pinchStartDistance = 0;
  private pinchStartZoom = 1;
  private pinchLastMid: { x: number; y: number } | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.container.classList.add('canvas-area');

    this.stage = document.createElement('div');
    this.stage.style.position = 'absolute';
    // Without explicit top/left, an absolutely positioned child of a flex container (.canvas-area
    // uses align-items/justify-content: center) gets its *static* position from that flex
    // alignment instead of the box's top-left corner -- so the translate/scale transform below,
    // which assumes (0,0) is the container's origin, would be offset by the flex-centering amount
    // on top of its own centering math, pushing the image out of the box.
    this.stage.style.top = '0';
    this.stage.style.left = '0';
    this.stage.style.transformOrigin = '0 0';
    // Nothing to show until setImage(): a fresh <canvas> is 300x150 by default and its drop
    // shadow painted a stray empty box in the workspace.
    this.stage.style.display = 'none';

    this.bgCanvas = document.createElement('canvas');
    this.imgCanvas = document.createElement('canvas');
    this.imgCanvas.setAttribute('role', 'img');
    this.imgCanvas.style.position = 'absolute';
    this.imgCanvas.style.top = '0';
    this.imgCanvas.style.left = '0';
    this.bgCanvas.style.position = 'relative';

    this.stage.appendChild(this.bgCanvas);
    this.stage.appendChild(this.imgCanvas);
    this.container.appendChild(this.stage);

    this.container.addEventListener('pointerdown', this.onPointerDown);
    this.container.addEventListener('pointermove', this.onPointerMove);
    this.container.addEventListener('pointerup', this.onPointerUp);
    this.container.addEventListener('pointercancel', this.onPointerUp);
    this.container.addEventListener('wheel', this.onWheel, { passive: false });
    // Middle-button drags would otherwise start the browser's autoscroll; context menu on
    // right-drag isn't wanted over the image either.
    this.container.addEventListener('auxclick', (e) => e.preventDefault());
    this.container.addEventListener('contextmenu', (e) => e.preventDefault());
    this.container.addEventListener('dblclick', this.onDoubleClick);

    // Holding space turns any drag into a pan (the usual hand-tool shortcut), without changing
    // the selected tool.
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'Space' || e.repeat) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return;
      this.spaceHeld = true;
      this.updateCursor();
      if (this.container.matches(':hover')) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => {
      if (e.code !== 'Space') return;
      this.spaceHeld = false;
      this.updateCursor();
    });
    window.addEventListener('blur', () => {
      this.spaceHeld = false;
      this.updateCursor();
    });

    // Re-fit whenever the container's own box changes size (orientation change, sidebar
    // toggling, window resize).
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        // Re-fit right away (the box was measured at a stale size otherwise, which once left a
        // freshly loaded photo drawn tiny or off-center) and again after the debounce, in case
        // a layout pass is still settling.
        this.layout();
        clearTimeout(this.resizeDebounce);
        this.resizeDebounce = setTimeout(() => this.layout(), 100);
      });
      observer.observe(this.container);
    }
    this.updateCursor();
  }

  setToolHandler(handler: PointerToolHandler | null): void {
    this.toolHandler = handler;
    this.updateCursor();
  }

  /** When true a one-finger / left-button drag pans the image instead of using the tool. */
  setPanTool(enabled: boolean): void {
    this.panTool = enabled;
    this.updateCursor();
  }

  /**
   * When true the tool only needs a click, so dragging with the left button pans freely and the
   * tool fires on release only if the pointer stayed put (within a few pixels).
   */
  setClickTool(enabled: boolean): void {
    this.clickTool = enabled;
  }

  /** Called whenever the zoom level changes, with the new zoom as a percent (100 = fit-to-box). */
  setOnZoomChange(handler: ((percent: number) => void) | null): void {
    this.onZoomChange = handler;
  }

  getZoomPercent(): number {
    return Math.round(this.zoomFactor * 100);
  }

  canZoomIn(): boolean {
    return this.zoomFactor < MAX_ZOOM - 1e-6;
  }

  canZoomOut(): boolean {
    return this.zoomFactor > MIN_ZOOM + 1e-6;
  }

  zoomIn(): void {
    this.setZoomFactor(this.zoomFactor * ZOOM_STEP);
  }

  zoomOut(): void {
    this.setZoomFactor(this.zoomFactor / ZOOM_STEP);
  }

  /** Back to fit-to-box and centered. */
  zoomReset(): void {
    this.panX = 0;
    this.panY = 0;
    this.setZoomFactor(1);
  }

  /**
   * Sets the zoom, keeping the image point under (anchorX, anchorY) (container pixels) fixed on
   * screen; without an anchor the box center is used.
   */
  private setZoomFactor(next: number, anchorX?: number, anchorY?: number): void {
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    if (this.width > 0 && anchorX !== undefined && anchorY !== undefined) {
      const rect = this.container.getBoundingClientRect();
      const ratio = clamped / this.zoomFactor;
      // Position of the anchor relative to the image center (which is where pan is measured from).
      const cx = rect.width / 2 + this.panX;
      const cy = rect.height / 2 + this.panY;
      this.panX += (anchorX - cx) * (1 - ratio);
      this.panY += (anchorY - cy) * (1 - ratio);
    }
    this.zoomFactor = clamped;
    this.layout();
    this.onZoomChange?.(this.getZoomPercent());
  }

  private panBy(dx: number, dy: number): void {
    this.panX += dx;
    this.panY += dy;
    this.layout();
  }

  setBackground(mode: PreviewBackground, customColor?: RgbColor): void {
    this.background = mode;
    if (customColor) this.customColor = customColor;
    this.drawBackground();
  }

  setImage(width: number, height: number, rgba: Uint8ClampedArray): void {
    this.width = width;
    this.height = height;
    this.bgCanvas.width = width;
    this.bgCanvas.height = height;
    this.imgCanvas.width = width;
    this.imgCanvas.height = height;
    this.stage.style.width = `${width}px`;
    this.stage.style.height = `${height}px`;
    this.stage.style.display = '';
    this.drawBackground();
    this.drawImage(rgba);
    // A newly loaded image always starts fit-to-box and centered, regardless of whatever zoom
    // level or pan was left over from a previous image.
    this.zoomFactor = 1;
    this.panX = 0;
    this.panY = 0;
    this.recomputeBaseScale();
    this.onZoomChange?.(this.getZoomPercent());
  }

  /** Removes the current image entirely (e.g. after the last file is closed), leaving an empty box. */
  clear(): void {
    this.width = 0;
    this.height = 0;
    this.bgCanvas.width = 0;
    this.bgCanvas.height = 0;
    this.imgCanvas.width = 0;
    this.imgCanvas.height = 0;
    this.stage.style.width = '0px';
    this.stage.style.height = '0px';
    this.stage.style.transform = '';
    this.stage.style.display = 'none';
    this.zoomFactor = 1;
    this.panX = 0;
    this.panY = 0;
    this.onZoomChange?.(this.getZoomPercent());
  }

  drawImage(rgba: Uint8ClampedArray): void {
    const ctx = this.imgCanvas.getContext('2d');
    if (!ctx) return;
    ctx.putImageData(new ImageData(rgba, this.width, this.height), 0, 0);
  }

  private drawBackground(): void {
    const ctx = this.bgCanvas.getContext('2d');
    if (!ctx || this.width === 0) return;
    if (this.background === 'checkerboard') {
      const tile = 12;
      for (let y = 0; y < this.height; y += tile) {
        for (let x = 0; x < this.width; x += tile) {
          const even = ((x / tile) | 0) % 2 === ((y / tile) | 0) % 2;
          ctx.fillStyle = even ? '#dddddd' : '#ffffff';
          ctx.fillRect(x, y, tile, tile);
        }
      }
    } else if (this.background === 'white') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, this.width, this.height);
    } else if (this.background === 'black') {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, this.width, this.height);
    } else {
      ctx.fillStyle = `rgb(${this.customColor.r}, ${this.customColor.g}, ${this.customColor.b})`;
      ctx.fillRect(0, 0, this.width, this.height);
    }
  }

  /** Re-fits to the box (e.g. after the box itself resizes); kept for callers, layout() measures fresh anyway. */
  recomputeBaseScale(): void {
    this.layout();
    // The box may still be mid-reflow (tab just shown, image just inserted): fit once more on
    // the next frame so the first paint is never based on a stale size.
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => this.layout());
  }

  /**
   * Applies zoom + pan on top of the fit-to-box scale, which is measured fresh from the box on
   * every call so a stale measurement can never stick. Pan is clamped so the image stays in the box.
   */
  private layout(): void {
    if (this.width === 0) return;
    const rect = this.container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const padding = 24;
    const availableW = Math.max(rect.width - padding, 1);
    const availableH = Math.max(rect.height - padding, 1);
    this.baseScale = Math.min(availableW / this.width, availableH / this.height);
    this.scale = this.baseScale * this.zoomFactor;
    const drawW = this.width * this.scale;
    const drawH = this.height * this.scale;
    // An axis that fits inside the box stays centered; one that overflows may be panned until
    // its far edge reaches the box edge (never further, so there's no empty gap to drift into).
    const maxPanX = Math.max(0, (drawW - rect.width) / 2);
    const maxPanY = Math.max(0, (drawH - rect.height) / 2);
    this.panX = Math.min(maxPanX, Math.max(-maxPanX, this.panX));
    this.panY = Math.min(maxPanY, Math.max(-maxPanY, this.panY));
    this.offsetX = (rect.width - drawW) / 2 + this.panX;
    this.offsetY = (rect.height - drawH) / 2 + this.panY;
    this.stage.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`;
    this.updateCursor();
  }

  private overflows(): boolean {
    if (this.width === 0) return false;
    const rect = this.container.getBoundingClientRect();
    return this.width * this.scale > rect.width + 0.5 || this.height * this.scale > rect.height + 0.5;
  }

  private clientToImagePoint(clientX: number, clientY: number): ImagePoint {
    const rect = this.container.getBoundingClientRect();
    const x = (clientX - rect.left - this.offsetX) / this.scale;
    const y = (clientY - rect.top - this.offsetY) / this.scale;
    return { x: Math.round(x), y: Math.round(y) };
  }

  private updateCursor(): void {
    const panning = this.panPointerId !== null;
    const panReady = !panning && this.width > 0 && (this.panTool || this.spaceHeld || !this.toolHandler);
    this.container.classList.toggle('panning', panning);
    this.container.classList.toggle('pan-ready', panReady);
  }

  private onWheel = (event: WheelEvent): void => {
    if (this.width === 0) return;
    const rect = this.container.getBoundingClientRect();
    event.preventDefault();
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1;
    if (event.deltaY !== 0) {
      // The scroll wheel zooms about the cursor (ctrl/cmd not required). A trackpad pinch arrives
      // as a wheel event with ctrlKey set and tiny deltas; a mouse notch is ~100 px. One notch ≈ 1.5x.
      const speed = event.ctrlKey || event.metaKey ? 0.01 : 0.004;
      const factor = Math.exp(-event.deltaY * unit * speed);
      this.setZoomFactor(this.zoomFactor * factor, event.clientX - rect.left, event.clientY - rect.top);
    }
    // A sideways swipe/tilt-wheel pans a zoomed-in image horizontally.
    if (event.deltaX !== 0 && this.overflows()) this.panBy(-event.deltaX * unit, 0);
  };

  private onDoubleClick = (event: MouseEvent): void => {
    // Double-click toggles between fit and a 2x look at the clicked spot -- only when panning is
    // what a click means here, so a double-tap with the wand doesn't zoom under the user.
    if (!(this.panTool || this.spaceHeld || !this.toolHandler) || this.width === 0) return;
    const rect = this.container.getBoundingClientRect();
    if (this.zoomFactor > MIN_ZOOM + 1e-6) this.zoomReset();
    else this.setZoomFactor(2, event.clientX - rect.left, event.clientY - rect.top);
  };

  private pinchDistance(): number {
    const pts = Array.from(this.activePointers.values());
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }

  private pinchMidpoint(): { x: number; y: number } | null {
    const pts = Array.from(this.activePointers.values());
    if (pts.length < 2) return null;
    const rect = this.container.getBoundingClientRect();
    return { x: (pts[0].x + pts[1].x) / 2 - rect.left, y: (pts[0].y + pts[1].y) / 2 - rect.top };
  }

  private startPan(event: PointerEvent): void {
    this.panPointerId = event.pointerId;
    this.panLast = { x: event.clientX, y: event.clientY };
    this.updateCursor();
  }

  private endPan(): void {
    this.panPointerId = null;
    this.panLast = null;
    this.updateCursor();
  }

  private onPointerDown = (event: PointerEvent): void => {
    try {
      this.container.setPointerCapture(event.pointerId);
    } catch {
      // A stale pointer id here is a lost nice-to-have (capture keeps drags tracking outside the
      // element), never a reason to abort the tool handling below.
    }
    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.activePointers.size === 2) {
      // A second finger landed: this is a pinch/two-finger pan, not a tool stroke -- cancel
      // whatever the first finger started so a wand/brush action doesn't fire underneath it.
      if (this.isPointerDown) {
        this.isPointerDown = false;
        this.toolHandler?.(this.clientToImagePoint(event.clientX, event.clientY), event, 'up');
      }
      this.endPan();
      this.pendingClick = null;
      this.pinchStartDistance = this.pinchDistance();
      this.pinchStartZoom = this.zoomFactor;
      this.pinchLastMid = this.pinchMidpoint();
      return;
    }
    if (this.activePointers.size > 2) return;
    if (this.width === 0) return;

    const wantsPan =
      event.button === 1 || // middle button
      event.button === 2 || // right button always drags the photo, whatever the tool
      this.spaceHeld ||
      this.panTool ||
      !this.toolHandler ||
      event.pointerType === 'touch' && !this.toolHandler;
    if (wantsPan) {
      event.preventDefault();
      this.startPan(event);
      return;
    }
    if (event.button !== 0) return;

    if (this.clickTool) {
      // Decide on release: a still click uses the tool, any real movement becomes a drag.
      this.pendingClick = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      return;
    }

    this.isPointerDown = true;
    const point = this.clientToImagePoint(event.clientX, event.clientY);
    this.toolHandler?.(point, event, 'down');
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (this.activePointers.has(event.pointerId)) {
      this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }

    if (this.activePointers.size === 2) {
      const distance = this.pinchDistance();
      const mid = this.pinchMidpoint();
      if (mid && this.pinchLastMid) {
        // Two fingers moving together pan; their spread zooms about the midpoint.
        this.panBy(mid.x - this.pinchLastMid.x, mid.y - this.pinchLastMid.y);
      }
      if (this.pinchStartDistance > 0 && distance > 0 && mid) {
        this.setZoomFactor(this.pinchStartZoom * (distance / this.pinchStartDistance), mid.x, mid.y);
      }
      this.pinchLastMid = mid;
      return;
    }

    if (this.pendingClick && this.pendingClick.pointerId === event.pointerId) {
      const moved = Math.hypot(event.clientX - this.pendingClick.x, event.clientY - this.pendingClick.y);
      if (moved < 4) return;
      // Moved: this is a drag, not a click. Pan from the original press point so no pixels are lost.
      const { x, y } = this.pendingClick;
      this.pendingClick = null;
      this.panPointerId = event.pointerId;
      this.panLast = { x, y };
      this.updateCursor();
    }

    if (this.panPointerId === event.pointerId && this.panLast) {
      if (event.buttons === 0 && event.pointerType === 'mouse') {
        this.endPan();
        return;
      }
      this.panBy(event.clientX - this.panLast.x, event.clientY - this.panLast.y);
      this.panLast = { x: event.clientX, y: event.clientY };
      return;
    }

    if (!this.isPointerDown || event.buttons === 0) return;
    const point = this.clientToImagePoint(event.clientX, event.clientY);
    this.toolHandler?.(point, event, 'move');
  };

  private onPointerUp = (event: PointerEvent): void => {
    this.activePointers.delete(event.pointerId);
    if (this.activePointers.size < 2) {
      this.pinchStartDistance = 0;
      this.pinchLastMid = null;
    }
    if (this.panPointerId === event.pointerId) {
      this.endPan();
      return;
    }
    if (this.pendingClick && this.pendingClick.pointerId === event.pointerId) {
      // A still click: run the tool as a down + up at the press point.
      const point = this.clientToImagePoint(this.pendingClick.x, this.pendingClick.y);
      this.pendingClick = null;
      this.toolHandler?.(point, event, 'down');
      this.toolHandler?.(point, event, 'up');
      return;
    }
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    const point = this.clientToImagePoint(event.clientX, event.clientY);
    this.toolHandler?.(point, event, 'up');
  };
}
